/**
 * Dropdown menu for the ELF-CIRCLE headers.
 *
 * Two jobs, one panel:
 *
 * - `burger` (default) is the narrow-viewport menu. The nav grew past what a
 *   phone header can hold — the links used to be `display: none` below 620px
 *   with nothing in their place, so every inner page became unreachable from a
 *   phone. This puts them behind a toggle instead of dropping them.
 * - `more` is the desktop overflow. The header shell is capped at 1536px, which
 *   leaves 1336px for links against the ~1514px the full set needs, so the row
 *   wrapped at every viewport — a wider monitor never helped. The lower-traffic
 *   entries live here instead, which keeps the row on one line at any width and
 *   absorbs the admin-only entry that used to push it over on its own.
 *
 * It renders in both headers (the hero's film-still nav and the inner-page
 * `.wnav`) and inherits its colour from `currentColor`, so each context keeps
 * its own treatment without a second copy of the markup.
 */

import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { route } from '../../../../shared/utils';
import type { NavItem } from './ElfPageNav';

export interface ElfNavMenuProps {
  /** Entries to list — the caller passes its own, including admin-only ones. */
  items: NavItem[];
  /** Path of the current page, so its entry shows the `here` state. */
  current?: string;
  /** `burger` is the narrow-viewport menu; `more` is the desktop overflow. */
  variant?: 'burger' | 'more';
}

export const ElfNavMenu: React.FC<ElfNavMenuProps> = ({ items, current, variant = 'burger' }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  // Both variants can be on the page at once, so the panel id has to be unique.
  const panelId = `elf-navmenu-panel-${useId()}`;
  const isMore = variant === 'more';
  // A page living behind `More` still marks the trigger, so the header never
  // looks like it has lost track of where the visitor is.
  const holdsCurrent = items.some((item) => item.to === current);

  // Navigating away should never leave the panel hanging open behind the page.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  if (isMore && items.length === 0) return null;

  return (
    <div className={isMore ? 'elf-navmenu elf-navmenu--more' : 'elf-navmenu'} ref={wrap}>
      <button
        type="button"
        className={isMore ? 'elf-navmenu__more' : 'elf-navmenu__btn'}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={isMore ? undefined : open ? 'Close menu' : 'Open menu'}
        data-here={isMore && holdsCurrent ? 'true' : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {isMore ? (
          <>
            More
            <span className="elf-navmenu__caret" data-open={open} aria-hidden="true" />
          </>
        ) : (
          <span className="elf-navmenu__bars" data-open={open} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        )}
      </button>

      {open && (
        <div className="elf-navmenu__panel" id={panelId}>
          {items.map((item) => (
            <Link
              key={item.to}
              to={route(item.to)}
              className={item.to === current ? 'elf-navmenu__link here' : 'elf-navmenu__link'}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ElfNavMenu;
